import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import { Button } from "react-bootstrap";
import { selectRequestSubTypesById } from "../../../../../redux/actions/lenderRequestActions";
import ReasonCodeTooltip from "../../../../SharedComponents/ReasonCodeTooltip/ReasonCodeTooltip";
import { REQUEST_TYPES } from "../../../../shared/constants/RequestTypes";
import { isEquivalent } from "../../../../shared/utilities/array-utils";

import "../CreateStatusReviewRequest.css";

const SUMMARY_ANALYSIS_MAX_LENGTH = 2000;

// Compares current reason code selections against previously-submitted formState
// to detect whether subsequent step state needs clearing.
// NOTE: This is separate from hasReasonCodeChanges (which validates against project's existing codes).
export const haveFormReasonCodesChanged = (newCodes, previousCodes) => {
  if (!previousCodes) return false;
  if (!newCodes) return previousCodes.length > 0;
  return !isEquivalent(newCodes, previousCodes, rc => rc.value);
};

const RequestDetailsPage = ({
  projectInfo,
  formState,
  incrementCounter,
  decrementCounter,
  projectId
}) => {
  const { handleSubmit, setValue, watch, register } = useForm({
    defaultValues: {
      addUnavailable: false,
      selectedReasonCodes: null,
      comments: ""
    }
  });

  const [loading, setLoading] = useState(false);
  const [allReasonCodes, setAllReasonCodes] = useState([]);
  const [existingReasonCodes, setExistingReasonCodes] = useState([]);
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [showCommentValidation, setShowCommentValidation] = useState(false);
  const [showReasonCodeValidation, setShowReasonCodeValidation] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [reasonCodeErrorMessage, setReasonCodeErrorMessage] = useState("");

  // get reason code to requestSubTypeId lookup from store
  const reasonCodeToRequestSubTypeLookup = useSelector(state => state.lenderRequestReducer.reasonCodeToRequestSubTypeLookup);

  // Watch form values
  const watchedAddUnavailable = watch("addUnavailable");
  const watchedReasonCodes = watch("selectedReasonCodes");
  const watchedComments = watch("comments");

  // Get current request type from form state
  const requestType = formState?.requestType;

  const dispatch = useDispatch();  

  // Restore form state if returning to this step
  useEffect(() => {
    if (formState) {
      if (formState.addUnavailable !== undefined) {
        setValue("addUnavailable", formState.addUnavailable);
      }
      if (formState.selectedReasonCodes) {
        setValue("selectedReasonCodes", formState.selectedReasonCodes);
      }
      if (formState.comments) {
        setValue("comments", formState.comments);
      }
    }
  }, [formState, setValue]);

  useEffect(() => {
    if (allReasonCodes.length && !existingReasonCodes.length &&
      (requestType === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS || requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
    ) {
      (async () => {
        const _existingReasonCodes = await getExistingReasonCodes(allReasonCodes);
        setExistingReasonCodes(_existingReasonCodes);
        setShowReasonCodeValidation(_existingReasonCodes?.length > 0);
        
        // For MODIFY_UNAVAILABLE_STATUS, pre-select existing reason codes if no codes are already selected
        if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && 
            _existingReasonCodes.length && 
            !watchedReasonCodes
        ) {
          const _selectedReasonCodes = _existingReasonCodes.map(code => ({
            label: code.reasonCode,
            value: code.reasonId
          }));
          setValue("selectedReasonCodes", _selectedReasonCodes);
        }
      })();
    }
  }, [requestType, allReasonCodes, formState, setValue]);

  useEffect(() => {
    if (requestType) {
      if (requestType === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) {
        setCanContinue(!!watchedComments?.trim());
      } else if (watchedReasonCodes != null) {
        if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
          setCanContinue(watchedReasonCodes.length && !!watchedComments?.trim());
        } else if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
          setCanContinue(watchedReasonCodes.length && hasReasonCodeChanges(watchedReasonCodes, existingReasonCodes) && !!watchedComments?.trim());
        } else {
          console.warn("Unknown request type for reason code validation:", requestType);
        }
      }
    }
  }, [requestType, watchedReasonCodes, watchedComments]);

  // Fetch reason codes 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const sortedReasonCodes = await getAllReasonCodes();

        setAllReasonCodes(sortedReasonCodes);
        
        const options = sortedReasonCodes.map(rc => ({
          label: rc.reasonCode,
          value: rc.reasonId
        }));

        setReasonCodeOptions(options);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [projectId]);

  const getAllReasonCodes = async () => {
    const reasonCodesResponse = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/reasonCodes/getAllReasonCodes`
    );

    const sortedReasonCodes = 
      (reasonCodesResponse.data || [])
        .filter(r => r.active)
        .sort(
          (a, b) => a.reasonCode.localeCompare(b.reasonCode)
        );

    return sortedReasonCodes;
  };

  const getExistingReasonCodes = async (allReasonCodes) => {
    try {
      setLoading(true);

      // Get FM Activity Feed data like Project.js does
      const { data } = await axios.get(
        global.REACT_APP_API_ENDPOINT +
        `/activityFeed/getFnmaReviewStatusActivityFeedForProject/` + projectId
      );
      
      const fmActivity = data.activityFeedFnmaStatusDTO || [];

      // Find the most recent entry
      const latestEntry = fmActivity[0];
      
      if (latestEntry?.reasonIds && latestEntry?.fnmaReviewStatus === 'Unavailable') {
        // Use already-fetched reason codes to map the IDs to full reason code objects
        const existingCodes = latestEntry.reasonIds
          .map(id => allReasonCodes.find(code => code.reasonId === id))
          .filter(Boolean) 
          .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
        
        return existingCodes;
      }
      
      return [];

    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleReasonCodeChange = (selectedOptions) => {
    setValue("selectedReasonCodes", selectedOptions || []);
    validateReasonCodes(selectedOptions);
  };

  const validateReasonCodes = (selectedReasonCodes) => {
    let validationMessage = "";

    if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
      if (!selectedReasonCodes?.length) {
        validationMessage = "At least one reason code is required."
      } else if (!hasReasonCodeChanges(selectedReasonCodes, existingReasonCodes)) {
        validationMessage = "You must change at least one reason code to submit this request.";
      } else {
        validationMessage = "";
      }
    } else if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
      if (!selectedReasonCodes?.length) {
        validationMessage = "At least one reason code is required.";
      } else {
        validationMessage = "";
      }
    }

    setReasonCodeErrorMessage(validationMessage);
    return !validationMessage;
  };

  // Checks whether the user has made any changes to the reason codes (vs the project's existing codes).
  // NOTE: This is separate from haveFormReasonCodesChanged (which detects drift between form submissions).
  const hasReasonCodeChanges = (selectedReasonCodes, existingReasonCodes) => {
    if (!selectedReasonCodes || !existingReasonCodes) {
      return false;
    }
    
    const selectedIds = new Set(selectedReasonCodes.map(rc => rc.value));
    const existingIds = new Set(existingReasonCodes.map(rc => rc.reasonId));
    
    if (selectedIds.size !== existingIds.size) {
      return true;
    }
    
    return ![...selectedIds].every((id) => existingIds.has(id));
  };

  const handleFormSubmit = (data) => {
    setShowCommentValidation(true);
    setShowReasonCodeValidation(true);

    const trimmedComments = (data.comments || "").trim();

    if (validateReasonCodes(data.selectedReasonCodes) && trimmedComments) {
      // Detect if reason codes have changed from previous submission
      const reasonCodesChanged = haveFormReasonCodesChanged(
        data.selectedReasonCodes,
        formState?.selectedReasonCodes
      );

      const formStateData = {
        ...formState,
        addUnavailable: data.addUnavailable,
        selectedReasonCodes: data.selectedReasonCodes,
        existingReasonCodes: existingReasonCodes,
        comments: trimmedComments,
      };

      // Clear subsequent step state if reason codes changed
      if (reasonCodesChanged) {
        delete formStateData.selectedFiles;
        delete formStateData.requiredDocuments;
      }
      
      // Calculate reason code IDs for document requirements
      let reasonCodeIds;

      if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
        // For Modify Unavailable, only include documents for added and removed reason codes
        // (codes that are unchanged do not require new documents)
        const selectedIds = new Set((formStateData.selectedReasonCodes || []).map(rc => rc.value));
        const existingIds = new Set((existingReasonCodes || []).map(rc => rc.reasonId));

        // Added codes: in selected but not in existing
        const addedIds = [...selectedIds].filter(id => !existingIds.has(id));
        // Removed codes: in existing but not in selected
        const removedIds = [...existingIds].filter(id => !selectedIds.has(id));

        reasonCodeIds = [...addedIds, ...removedIds];
      } else if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
        reasonCodeIds = (formStateData.selectedReasonCodes || []).map(rc => rc.value);
      } else {
        // Remove Unavailable - use existing codes
        reasonCodeIds = (existingReasonCodes || []).map(rc => rc.reasonId);
      }
      
      const requestSubTypeIds = reasonCodeIds.map(rc => reasonCodeToRequestSubTypeLookup.get(rc)).filter(id => id != null);
      
      dispatch(selectRequestSubTypesById(requestSubTypeIds));

      incrementCounter(formStateData);
    }
  };

  const handleBack = () => {
    const currentData = {
      ...formState,
      addUnavailable: watchedAddUnavailable,
      selectedReasonCodes: watchedReasonCodes,
      comments: watchedComments
    };
    decrementCounter(currentData);
  };

  return (
    <Grid item xs={12} sm={12} md={10}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid item xs={12} sm={12} md={10}>
          <h1>Request Details</h1>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <h6>Step 2 of 5 - Request Configuration</h6>
          <p><strong>Request Type:</strong> {requestType}</p>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={10}>
            <div className="form-section reason-codes">
              <h3>
                Reason Codes
                {allReasonCodes?.length ? <ReasonCodeTooltip reasonCodes={allReasonCodes} /> : <></>}
              </h3>
              {!allReasonCodes?.length || loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading reason codes...
                </div>
              ) : (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS || 
                  requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
              ) ? (
                <>
                  <p>
                    {requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS 
                      ? "Select the updated reason codes for this project. At least one reason code is required."
                      : "Select the reason codes that apply to this request:"}
                    <span style={{ color: "red" }}>*</span>
                  </p>
                  {requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && existingReasonCodes?.length && (
                    <div style={{ 
                      backgroundColor: '#f0f8ff', 
                      border: '1px solid #b3d9ff', 
                      borderRadius: '4px', 
                      padding: '12px', 
                      marginBottom: '15px' 
                    }}>
                      <strong>Existing Reason Codes:</strong>
                      <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
                        {existingReasonCodes.map((rc) => (
                          <li key={rc.reasonId}>{rc.reasonCode}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="reason-codes-container">
                    <ReactSelect
                      name="selectedReasonCodes"
                      className="pers-reason-codes"
                      options={reasonCodeOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      value={watchedReasonCodes || []}
                      onChange={handleReasonCodeChange}
                      allowSelectAll={false}
                      placeholder="Select reason codes..."
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "45px",
                          borderColor:
                            showReasonCodeValidation && reasonCodeErrorMessage
                              ? "#d02f2f"
                              : state.isFocused
                              ? "#007bff"
                              : "#e6e8ed"
                        })
                      }}
                    />
                  </div>
                  {showReasonCodeValidation && reasonCodeErrorMessage && (
                    <div className="validation-error">
                      {reasonCodeErrorMessage}
                    </div>
                  )}
                </>
              ) : existingReasonCodes?.length ? (
                <>
                  <p>
                    Fannie Mae will require documentation based on the existing reason codes associated with this project:
                  </p>
                  <ul className="reason-codes-list">
                    {existingReasonCodes.map((rc) => (
                      <li key={rc.reasonId}>{rc.reasonCode}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="no-reason-codes-message">
                  Note: No existing reason codes were found for this project's current Unavailable status.
                </p>
              )}
            </div>
            <div className="form-section comments">
              <h3>Summary & Analysis<span style={{ color: "red" }}>*</span></h3>
              <p>Provide a summary and analysis for this request:</p>
              <textarea
                {...register("comments")}
                placeholder="Enter your summary and analysis here..."
                maxLength={SUMMARY_ANALYSIS_MAX_LENGTH}
                className="comments-textarea"
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                {showCommentValidation && !watchedComments?.trim() && (
                  <div className="validation-error" style={{ margin: 0 }}>Summary & Analysis is required.</div>
                )}
                <div style={{ marginLeft: "auto", fontSize: "12px", color: "#666" }}>
                  {(watchedComments?.length || 0)}/{SUMMARY_ANALYSIS_MAX_LENGTH}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={2} className="dialoguClass">
          <Grid item xs={12} sm={12} md={10}>
            <Grid container justifyContent="space-between">
              <Button
                type="submit"
                className="back-button"
                style={{width: "150px"}}
                onClick={handleBack}
                form="addProjectForm"
              >
                Back
              </Button>
              <Button
                type="submit"
                className={"btn-submit" + (!canContinue ? " btn-invalid" : "")}
              >
                Next
              </Button>            
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default RequestDetailsPage;